// eslint-disable-next-line @tseslint/no-shadow
import { ChangeDetectionStrategy, Component, inject, model, ModelSignal, signal, WritableSignal, OnInit, Signal, computed } from '@angular/core';
import { SideMenuComponent } from "@shared/components/side-menu/side-menu.component";
import { SideMenuContentComponent } from "@shared/components/side-menu/components/side-menu-content/side-menu-content.component";
import { provideTranslationScope, TranslationPipeModule } from '@ogs-gmbh/ngx-translate';
import { ProcessNotificationComponent } from "../process-notification/process-notification.component";
import { Process, PROCESS_STATUS, ProcessNotification } from '@core/types/process.types';
import { NotificationMenuService } from './services/notification-menu.service';
import { ProcessService } from '@core/services/process/process.service';
import { Subject, throttleTime } from 'rxjs';
import { SideMenuHeaderComponent } from '@shared/components/side-menu/components/side-menu-header/side-menu-header.component';
import { SideMenuTitleComponent } from "@shared/components/side-menu/components/side-menu-title/side-menu-title.component";
import { MatButtonModule } from '@angular/material/button';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProcessNotificationIsSeenChange } from '../process-notification/types/process-notification-is-seen-change.type';

@Component({
  selector: 'app-notification-menu',
  standalone: true,
  imports: [ SideMenuComponent,
    SideMenuContentComponent,
    TranslationPipeModule,
    SideMenuHeaderComponent,
    SideMenuTitleComponent,
    MatButtonModule,
    ScrollingModule,
    ProcessNotificationComponent ],
  providers: [ provideTranslationScope("core/notification-menu") ],
  templateUrl: './notification-menu.component.html',
  styleUrl: './notification-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationMenuComponent implements OnInit {
  private readonly _notificationMenuService: NotificationMenuService = inject(NotificationMenuService);

  private readonly _processService: ProcessService = inject(ProcessService);

  protected processNotifications: WritableSignal<ProcessNotification[]> = signal([]);

  private _processNotificationSeen$: Subject<ProcessNotificationIsSeenChange> = new Subject<ProcessNotificationIsSeenChange>();

  private _allProcessNotificationSeen$: Subject<void> = new Subject<void>();


  // Todo separate concerns
  // eslint-disable-next-line @unicorn/consistent-function-scoping
  protected isMarkAllSeenDisabled: Signal<boolean> = computed(() => this.processNotifications().length === 0 || !this.processNotifications()
    .some((notification: ProcessNotification) => notification.process.status === PROCESS_STATUS.readyToDownload && !notification.isSeen));

  protected isMenuExpanded: ModelSignal<boolean> = model(false);

  constructor () {
    this._processService.processNotifications$
      .pipe(takeUntilDestroyed())
      .subscribe((processNotifications: ProcessNotification[]) => {
        const sorted: ProcessNotification[] = this.sortProcessNotifications(processNotifications);

        this.processNotifications.set(sorted);
      });
  }

  public ngOnInit (): void {
    this._notificationMenuService.onMenuOpen$.subscribe(() => {
      this.isMenuExpanded.set(true);
    });
    this._processNotificationSeen$.pipe(
      throttleTime(1000, undefined, { leading: true, trailing: true })
    ).subscribe((processNotificationSeenEvent: ProcessNotificationIsSeenChange) => {
      this._processService.isProcessSeen(processNotificationSeenEvent.processId, processNotificationSeenEvent.isSeen).subscribe();
    });
    this._allProcessNotificationSeen$.pipe(
      throttleTime(1000, undefined, { leading: true, trailing: true })
    ).subscribe(() => {
      this._processService.markAllProcessesSeen().subscribe();
    });
  }


  // eslint-disable-next-line @tseslint/class-methods-use-this
  private sortProcessNotifications (processNotifications: ProcessNotification[]): ProcessNotification[] {
    return processNotifications.sort((a: ProcessNotification, b: ProcessNotification) => (a.process.id < b.process.id ? -1 : 1));
  }


  protected setProgressNotificationSeen (changeEvent: ProcessNotificationIsSeenChange): void {
    this._processNotificationSeen$.next({ isSeen: changeEvent.isSeen, processId: changeEvent.processId });
  }

  protected pauseProcess (processId: string): void {
    this._processService.pauseProcess(processId).subscribe();
  }

  protected deleteProcessNotification (processId: string): void {
    this._processService.deleteProcess(processId).subscribe();
  }

  protected restartProcess (processId: string): void {
    this._processService.restartProcess(processId).subscribe();
  }

  protected downloadProcess (process: Process): void {
    this._processService.downloadProcess(process).subscribe();
  }

  protected markAllProcessNotificationsSeen (): void {
    this._allProcessNotificationSeen$.next();
  }

  // eslint-disable-next-line @tseslint/no-unused-vars, @tseslint/class-methods-use-this
  protected identifyProcess (index: number, processNotification: ProcessNotification): string {
    return processNotification.process.id;
  }
}
